import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  TableChart as TableViewIcon,
  InsertChartOutlined as ReportViewIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PaginationState, SortingState, VisibilityState } from '@tanstack/react-table';
import { billingApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasAdminAccess } from '../utils/AdminLogic';
import { SEARCE_COLORS } from '../theme';
import type { BillingBudget, BillingBudgetField } from '../types/billing';
import { BILLING_TABLE_COLUMNS } from '../types/billing';
import {
  applyBillingFieldUpdate,
  buildBillingUpdatePayload,
  buildGcpBudgetUpsertRequest,
  currentMonthBudgetChanged,
  getCurrentMonthBudgetField,
  getEditableBillingFields,
  getGcpBudgetUpsertFailureReason,
  isEditableBillingField,
} from '../utils/billingHelpers';
import {
  getOverspendUntilCurrentMonth,
  normalizeMonthlyBudgetEdits,
} from '../utils/billingBudgetValidation';
import {
  buildDefaultColumnVisibility,
  columnVisibilityForPreset,
  countDisplayLeafColumns,
  getBillingColumnLabel,
  type BillingColumnPresetId,
} from '../utils/billingTableConfig';
import {
  applyBillingColumnFilters,
  applyBillingQuickFilters,
  applyBillingSearch,
  getBillingCellDisplayText,
  hasActiveBillingFilters,
  type BillingColumnFilterMenuState,
  type BillingQuickFilter,
} from '../utils/billingTableFilters';
import { exportCsvWithVisibleColumns } from '../utils/csvExport';
import { SandboxBillingToolbar } from '../components/sandboxBilling/SandboxBillingToolbar';
import { SandboxBillingTable } from '../components/sandboxBilling/SandboxBillingTable';
import { AddBillingAccountDialog } from '../components/sandboxBilling/AddBillingAccountDialog';
import type { BillingBudgetCreate } from '../types/billing';
import { isAxiosError } from 'axios';
import { useBillingBusinessUnitOptions } from '../hooks/useBillingBusinessUnitOptions';
import { SandboxBillingReportView } from '../components/sandboxBilling/report/SandboxBillingReportView';

type ActiveCell = { accountId: string; field: BillingBudgetField } | null;

const GCPSandboxBillingPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canEdit = hasAdminAccess(user);
  const editableFields = useMemo(() => getEditableBillingFields(), []);
  const { businessUnitNames, getSubBuNames } = useBillingBusinessUnitOptions(canEdit);

  const [pendingEdits, setPendingEdits] = useState<Record<string, BillingBudget>>({});
  const [activeCell, setActiveCell] = useState<ActiveCell>(null);
  const [draftValue, setDraftValue] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterMenus, setFilterMenus] = useState<Record<string, BillingColumnFilterMenuState>>({});
  const [quickFilters, setQuickFilters] = useState<Set<BillingQuickFilter>>(new Set());
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() =>
    buildDefaultColumnVisibility(),
  );
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [editMode, setEditMode] = useState(false);
  const [selectedAccountIds, setSelectedAccountIds] = useState<Set<string>>(new Set());
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [rowActionBusy, setRowActionBusy] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'report'>('table');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sandbox-billing'],
    queryFn: () => billingApi.getSandboxBudgets(),
  });

  // Attach the client-derived cumulative overspend so it flows through filtering,
  // sorting, and CSV export like any other numeric column.
  const budgets = useMemo(
    () =>
      (data?.budgets ?? []).map((r) => ({
        ...r,
        cumulative_overspend: getOverspendUntilCurrentMonth(r),
      })),
    [data],
  );
  const originalsById = useMemo(
    () => new Map(budgets.map((r) => [r.account_id, r])),
    [budgets],
  );

  const filteredRows = useMemo(() => {
    let rows = applyBillingSearch(budgets, search);
    rows = applyBillingColumnFilters(rows, filterMenus);
    rows = applyBillingQuickFilters(rows, quickFilters);
    return rows;
  }, [budgets, search, filterMenus, quickFilters]);

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [search, filterMenus, quickFilters, sorting]);

  const pendingChangeCount = useMemo(() => {
    let count = 0;
    for (const [accountId, edited] of Object.entries(pendingEdits)) {
      const original = originalsById.get(accountId);
      if (!original) continue;
      const payload = buildBillingUpdatePayload(original, edited);
      count += Object.keys(payload).length;
    }
    return count;
  }, [pendingEdits, originalsById]);

  const hasPendingChanges = pendingChangeCount > 0;

  const getDisplayRow = useCallback(
    (row: BillingBudget): BillingBudget => {
      const r = pendingEdits[row.account_id] ?? row;
      // Recompute so the column reflects in-progress (unsaved) budget edits.
      return { ...r, cumulative_overspend: getOverspendUntilCurrentMonth(r) };
    },
    [pendingEdits],
  );

  const isCellDirty = useCallback(
    (accountId: string, field: BillingBudgetField): boolean => {
      const original = originalsById.get(accountId);
      const edited = pendingEdits[accountId];
      if (!original || !edited) return false;
      return !Object.is(original[field], edited[field]);
    },
    [originalsById, pendingEdits],
  );

  const upsertPendingRow = useCallback(
    (accountId: string, field: BillingBudgetField, raw: string) => {
      const original = originalsById.get(accountId);
      if (!original) return;

      setPendingEdits((prev) => {
        const base = prev[accountId] ?? original;
        const result = applyBillingFieldUpdate(base, field, raw);
        if (!result.success) {
          setSnackbar({ open: true, message: result.error.message, severity: 'error' });
          return prev;
        }
        if (result.infoMessage) {
          setSnackbar({ open: true, message: result.infoMessage, severity: 'success' });
        }
        const updated = result.row;
        const payload = buildBillingUpdatePayload(original, updated);
        const next = { ...prev };
        if (Object.keys(payload).length === 0) {
          delete next[accountId];
        } else {
          next[accountId] = updated;
        }
        return next;
      });
    },
    [originalsById, setSnackbar],
  );

  const handleCellDoubleClick = useCallback(
    (row: BillingBudget, field: BillingBudgetField) => {
      if (!canEdit || !editMode || field === 'account_id' || !isEditableBillingField(field)) return;

      if (field === 'is_flagged') {
        const display = getDisplayRow(row);
        upsertPendingRow(row.account_id, field, display.is_flagged ? 'false' : 'true');
        return;
      }

      const display = getDisplayRow(row);
      const raw =
        display[field] === null || display[field] === undefined ? '' : String(display[field]);
      setDraftValue(raw);
      setActiveCell({ accountId: row.account_id, field });
    },
    [canEdit, editMode, getDisplayRow, upsertPendingRow],
  );

  const commitActiveCell = useCallback(() => {
    if (!activeCell) return;
    upsertPendingRow(activeCell.accountId, activeCell.field, draftValue);
    setActiveCell(null);
    setDraftValue('');
  }, [activeCell, draftValue, upsertPendingRow]);

  const cancelActiveCell = useCallback(() => {
    setActiveCell(null);
    setDraftValue('');
  }, []);

  const handleDropdownCommit = useCallback(
    (row: BillingBudget, field: BillingBudgetField, value: string) => {
      upsertPendingRow(row.account_id, field, value);
      setActiveCell(null);
      setDraftValue('');
    },
    [upsertPendingRow],
  );

  const handleCancelAll = () => {
    setPendingEdits({});
    cancelActiveCell();
    setEditMode(false);
    setSelectedAccountIds(new Set());
  };

  const isRowSelected = useCallback(
    (accountId: string) => selectedAccountIds.has(accountId),
    [selectedAccountIds],
  );

  const toggleRowSelect = useCallback((accountId: string) => {
    setSelectedAccountIds((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
      }
      return next;
    });
  }, []);

  const toggleEditMode = useCallback(() => {
    setEditMode((prev) => {
      if (prev) {
        setSelectedAccountIds(new Set());
        setActiveCell(null);
        setDraftValue('');
      }
      return !prev;
    });
  }, []);

  const handleDeleteSelected = async () => {
    const ids = Array.from(selectedAccountIds);
    if (ids.length === 0) return;
    setRowActionBusy(true);
    try {
      const { deleted_count } = await billingApi.bulkDeleteSandboxBudgets(ids);
      await queryClient.invalidateQueries({ queryKey: ['sandbox-billing'] });
      setSelectedAccountIds(new Set());
      setDeleteConfirmOpen(false);
      setSnackbar({
        open: true,
        message: `Deleted ${deleted_count} account${deleted_count === 1 ? '' : 's'}.`,
        severity: 'success',
      });
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string })?.detail
        : undefined;
      setSnackbar({
        open: true,
        message: detail ?? 'Failed to delete selected accounts.',
        severity: 'error',
      });
    } finally {
      setRowActionBusy(false);
    }
  };

  const handleAddAccount = async (payload: BillingBudgetCreate) => {
    try {
      await billingApi.createSandboxBudget(payload);
      await queryClient.invalidateQueries({ queryKey: ['sandbox-billing'] });
      setSnackbar({
        open: true,
        message: `Created account ${payload.account_id}.`,
        severity: 'success',
      });
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string })?.detail
        : undefined;
      // Re-throw so the dialog can surface the error and stay open.
      throw new Error(detail ?? 'Failed to create account.');
    }
  };

  const handleSave = async () => {
    const effectiveEdits = { ...pendingEdits };
    if (activeCell) {
      const original = originalsById.get(activeCell.accountId);
      if (original) {
        const base = effectiveEdits[activeCell.accountId] ?? original;
        const result = applyBillingFieldUpdate(base, activeCell.field, draftValue);
        if (!result.success) {
          setSnackbar({ open: true, message: result.error.message, severity: 'error' });
          return;
        }
        const updated = result.row;
        const payload = buildBillingUpdatePayload(original, updated);
        if (Object.keys(payload).length === 0) {
          delete effectiveEdits[activeCell.accountId];
        } else {
          effectiveEdits[activeCell.accountId] = updated;
        }
      }
      setActiveCell(null);
      setDraftValue('');
    }

    const normalizedEdits: Record<string, BillingBudget> = {};
    for (const [accountId, edited] of Object.entries(effectiveEdits)) {
      const original = originalsById.get(accountId);
      if (!original) continue;
      const result = normalizeMonthlyBudgetEdits(original, edited);
      if (!result.ok) {
        setSnackbar({ open: true, message: result.error.message, severity: 'error' });
        return;
      }
      normalizedEdits[accountId] = result.row;
    }

    const toUpdate: { accountId: string; payload: Partial<BillingBudget> }[] = [];
    for (const [accountId, edited] of Object.entries(normalizedEdits)) {
      const original = originalsById.get(accountId);
      if (!original) continue;
      const payload = buildBillingUpdatePayload(original, edited);
      if (Object.keys(payload).length > 0) {
        toUpdate.push({ accountId, payload });
      }
    }

    if (toUpdate.length === 0) {
      setSnackbar({ open: true, message: 'No changes to save.', severity: 'success' });
      return;
    }

    const gcpUpsertTargets = toUpdate.filter(({ accountId }) => {
      const original = originalsById.get(accountId);
      const edited = normalizedEdits[accountId];
      if (!original || !edited) return false;
      return currentMonthBudgetChanged(original, edited);
    });

    for (const { accountId } of gcpUpsertTargets) {
      const edited = normalizedEdits[accountId];
      const monthField = getCurrentMonthBudgetField();
      const failureReason = getGcpBudgetUpsertFailureReason(edited);
      const upsertRequest = buildGcpBudgetUpsertRequest(edited);
      if (!upsertRequest) {
        const monthLabel = monthField.replace('_budget', '');
        const detail =
          failureReason === 'missing_currency'
            ? `set a currency for this account`
            : `set a valid ${monthLabel} budget greater than 0`;
        setSnackbar({
          open: true,
          message: `Cannot save ${accountId}: ${detail} before syncing GCP budget.`,
          severity: 'error',
        });
        return;
      }
    }

    setSaving(true);
    let failed = 0;
    try {
      for (const { accountId } of gcpUpsertTargets) {
        const edited = normalizedEdits[accountId];
        const upsertRequest = buildGcpBudgetUpsertRequest(edited)!;
        try {
          await billingApi.upsertGcpBudget(upsertRequest);
        } catch (err) {
          const detail = isAxiosError(err)
            ? (err.response?.data as { detail?: string })?.detail
            : undefined;
          setSnackbar({
            open: true,
            message:
              detail ??
              `Failed to sync GCP budget for ${accountId}. Changes were not saved.`,
            severity: 'error',
          });
          return;
        }
      }

      for (const { accountId, payload } of toUpdate) {
        try {
          await billingApi.updateSandboxBudget(accountId, payload);
        } catch {
          failed += 1;
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['sandbox-billing'] });
      if (failed > 0) {
        setSnackbar({
          open: true,
          message: `GCP budgets synced, but ${failed} row(s) failed to save locally.`,
          severity: 'error',
        });
      } else {
        setSnackbar({
          open: true,
          message:
            gcpUpsertTargets.length > 0
              ? `Synced ${gcpUpsertTargets.length} GCP budget(s) and saved ${toUpdate.length} row(s).`
              : `Saved ${toUpdate.length} row(s) successfully.`,
          severity: 'success',
        });
        setPendingEdits({});
        cancelActiveCell();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleFilterClick = useCallback(
    (columnId: BillingBudgetField, event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setFilterMenus((prev) => ({
        ...prev,
        [columnId]: {
          anchorEl: event.currentTarget,
          selectedValues: prev[columnId]?.selectedValues ?? [],
        },
      }));
    },
    [],
  );

  const handleFilterClose = useCallback((columnId: string) => {
    setFilterMenus((prev) => ({
      ...prev,
      [columnId]: { ...prev[columnId], anchorEl: null },
    }));
  }, []);

  const handleFilterSelect = useCallback((columnId: string, selectedValues: string[]) => {
    setFilterMenus((prev) => ({
      ...prev,
      [columnId]: { anchorEl: null, selectedValues },
    }));
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  const handleQuickFilterToggle = useCallback((filter: BillingQuickFilter) => {
    setQuickFilters((prev) => {
      const next = new Set(prev);
      if (next.has(filter)) {
        next.delete(filter);
      } else {
        next.add(filter);
      }
      return next;
    });
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setSearch('');
    setFilterMenus({});
    setQuickFilters(new Set());
    setSorting([]);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  const handlePresetApply = useCallback((preset: BillingColumnPresetId) => {
    setColumnVisibility(columnVisibilityForPreset(preset));
  }, []);

  const handleExport = useCallback(() => {
    const columnLabels = Object.fromEntries(
      BILLING_TABLE_COLUMNS.map((c) => [c.id, getBillingColumnLabel(c.id)]),
    );
    exportCsvWithVisibleColumns({
      rows: filteredRows.map((row) => getDisplayRow(row)),
      columnVisibility,
      columnIdsInOrder: BILLING_TABLE_COLUMNS.map((c) => c.id),
      columnLabels,
      getCellText: (row, columnId) =>
        getBillingCellDisplayText(row, columnId as BillingBudgetField),
      filenameBase: 'gcp-sandbox-billing',
    });
  }, [filteredRows, columnVisibility, getDisplayRow]);

  const showClearFilters = hasActiveBillingFilters(search, filterMenus, quickFilters);

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color={SEARCE_COLORS.primary}>
            GCP Sandbox Billing
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data?.total_count ?? 0} accounts
            {viewMode === 'table' && ' · Search, filter, and customize columns'}
            {viewMode === 'table' && canEdit && editMode && ' · Click a highlighted cell to edit'}
            {viewMode === 'table' && canEdit && !editMode && ' · Click "Edit Table" to make changes'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={viewMode}
            onChange={(_e, v) => v && setViewMode(v)}
          >
            <ToggleButton value="table" sx={{ px: 1.5 }}>
              <TableViewIcon sx={{ fontSize: 18, mr: 0.5 }} />
              Table
            </ToggleButton>
            <ToggleButton value="report" sx={{ px: 1.5 }}>
              <ReportViewIcon sx={{ fontSize: 18, mr: 0.5 }} />
              Report
            </ToggleButton>
          </ToggleButtonGroup>
          {viewMode === 'table' && canEdit && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {hasPendingChanges && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  onClick={handleCancelAll}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  Save ({pendingChangeCount})
                </Button>
              </>
            )}
            {editMode && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setAddDialogOpen(true)}
                  disabled={rowActionBusy}
                >
                  Add Account
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={
                    rowActionBusy ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />
                  }
                  onClick={() => setDeleteConfirmOpen(true)}
                  disabled={selectedAccountIds.size === 0 || rowActionBusy}
                >
                  Delete ({selectedAccountIds.size})
                </Button>
              </>
            )}
            {!hasPendingChanges && (
              <Button
                variant={editMode ? 'contained' : 'outlined'}
                startIcon={editMode ? <CloseIcon /> : <EditIcon />}
                onClick={toggleEditMode}
              >
                {editMode ? 'Cancel' : 'Edit Table'}
              </Button>
            )}
          </Box>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load billing data.{' '}
          <Button size="small" onClick={() => refetch()}>
            Retry
          </Button>
        </Alert>
      )}

      {viewMode === 'report' ? (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <SandboxBillingReportView rows={budgets} isLoading={isLoading} />
        </Box>
      ) : (
        <>
          <SandboxBillingToolbar
            search={search}
            onSearchChange={(v) => {
              setSearch(v);
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
            quickFilters={quickFilters}
            onQuickFilterToggle={handleQuickFilterToggle}
            showClearFilters={showClearFilters}
            onClearAllFilters={handleClearAllFilters}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            onPresetApply={handlePresetApply}
            onExport={handleExport}
            exportDisabled={filteredRows.length === 0}
            totalCount={budgets.length}
            filteredCount={filteredRows.length}
            visibleColumnCount={countDisplayLeafColumns(columnVisibility)}
          />

          <SandboxBillingTable
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            rows={filteredRows}
            allRowsForFilters={budgets}
            isLoading={isLoading}
            sorting={sorting}
            onSortingChange={setSorting}
            pagination={pagination}
            onPaginationChange={setPagination}
            filterMenus={filterMenus}
            onFilterClick={handleFilterClick}
            onFilterClose={handleFilterClose}
            onFilterSelect={handleFilterSelect}
            canEdit={canEdit}
            editableFields={editableFields}
            activeCell={activeCell}
            draftValue={draftValue}
            onDraftChange={setDraftValue}
            onCellDoubleClick={handleCellDoubleClick}
            onCommitActiveCell={commitActiveCell}
            onCancelActiveCell={cancelActiveCell}
            getDisplayRow={getDisplayRow}
            isCellDirty={isCellDirty}
            onClearFilters={handleClearAllFilters}
            editMode={editMode}
            isRowSelected={isRowSelected}
            onToggleRowSelect={toggleRowSelect}
            businessUnitNames={businessUnitNames}
            getSubBuNames={getSubBuNames}
            onDropdownCommit={handleDropdownCommit}
          />
        </>
      )}

      <AddBillingAccountDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSubmit={handleAddAccount}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !rowActionBusy && setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete {selectedAccountIds.size} account
          {selectedAccountIds.size === 1 ? '' : 's'}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently remove the selected billing account
            {selectedAccountIds.size === 1 ? '' : 's'} and all associated budget and spend
            data. This action cannot be undone.
          </DialogContentText>
          <Box
            sx={{
              mt: 2,
              maxHeight: 160,
              overflowY: 'auto',
              fontSize: '0.8125rem',
              color: 'text.secondary',
            }}
          >
            {Array.from(selectedAccountIds).map((id) => (
              <Box key={id} sx={{ py: 0.25 }}>
                {id}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={rowActionBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteSelected}
            disabled={rowActionBusy}
            startIcon={
              rowActionBusy ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GCPSandboxBillingPage;
